import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPublicPage, trackPageVisit } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';
import { AppButton } from '../components/apps';

// CSS Variables matching the template exactly
const cssVars = {
  bg: '#FAFAFA',
  bgWhite: '#FFFFFF',
  text: '#1a1a1a',
  textSecondary: '#6b6b6b',
  textMuted: '#999999',
  accent: '#E8553E',
  accentLight: '#FEF4F2',
  border: '#EBEBEB',
  borderHover: '#D4D4D4',
};

// Tag color palette - vibrant but professional colors
const TAG_COLORS = [
  { bg: '#DBEAFE', text: '#1E40AF' }, // Blue
  { bg: '#D1FAE5', text: '#065F46' }, // Green
  { bg: '#FEE2E2', text: '#991B1B' }, // Red
  { bg: '#E0E7FF', text: '#3730A3' }, // Indigo
  { bg: '#FCE7F3', text: '#9D174D' }, // Pink
  { bg: '#FEF3C7', text: '#92400E' }, // Amber
  { bg: '#CFFAFE', text: '#155E75' }, // Cyan
  { bg: '#F3E8FF', text: '#6B21A8' }, // Purple
  { bg: '#ECFCCB', text: '#3F6212' }, // Lime
  { bg: '#FFEDD5', text: '#9A3412' }, // Orange
];

// Get consistent color for a tag based on its text
const getTagColor = (tag, index) => {
  // Use a simple hash of the tag text to get consistent colors
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
};

// Render extras (free text, tags, links) for any card
const renderExtras = (extras, compact = false) => {
  if (!extras || (!extras.freeText && !extras.tags?.length && !extras.links?.length)) {
    return null;
  }

  return (
    <div style={{ marginTop: compact ? '12px' : '16px', paddingTop: compact ? '12px' : '16px', borderTop: compact ? 'none' : `1px solid ${cssVars.border}` }}>
      {/* Free text */}
      {extras.freeText && (
        <p style={{ fontSize: '0.9rem', color: cssVars.textSecondary, lineHeight: 1.6, marginBottom: extras.tags?.length || extras.links?.length ? '12px' : 0 }}>
          {extras.freeText}
        </p>
      )}

      {/* Tags */}
      {extras.tags?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: extras.links?.length ? '12px' : 0 }}>
          {extras.tags.map((tag, i) => {
            const color = getTagColor(tag);
            return (
              <span
                key={i}
                style={{
                  padding: '4px 10px',
                  borderRadius: '100px',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  backgroundColor: color.bg,
                  color: color.text
                }}
              >
                {tag}
              </span>
            );
          })}
        </div>
      )}

      {/* Links with previews */}
      {extras.links?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {extras.links.filter(l => l.url).map((link, i) => (
            <a
              key={i}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 12px',
                borderRadius: '12px',
                border: `1px solid ${cssVars.border}`,
                backgroundColor: cssVars.bg,
                textDecoration: 'none',
                transition: 'all 0.2s ease'
              }}
            >
              {link.image && (
                <img
                  src={link.image}
                  alt=""
                  style={{
                    width: '48px',
                    height: '48px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    flexShrink: 0
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ 
                    fontWeight: '500', 
                    fontSize: '0.875rem', 
                    color: cssVars.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {link.title || link.url}
                  </span>
                  <svg 
                    width="12" 
                    height="12" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke={cssVars.textMuted} 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    style={{ flexShrink: 0 }}
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </div>
                {link.description && (
                  <p style={{ 
                    fontSize: '0.75rem', 
                    color: cssVars.textMuted, 
                    marginTop: '2px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {link.description}
                  </p>
                )}
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default function PublicProfilePage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const visitTracked = useRef(false);

  const loadPage = useCallback(async () => {
    try {
      const data = await getPublicPage(slug);
      setPage(data);
    } catch (err) {
      setError('Page not found');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  // Track page visit once when component mounts
  useEffect(() => {
    if (slug && !visitTracked.current) {
      visitTracked.current = true;
      trackPageVisit(slug).catch(() => {
        // Silently fail - analytics shouldn't break the page
      });
    }
  }, [slug]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: cssVars.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: cssVars.text }} />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div style={{ minHeight: '100vh', background: cssVars.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 className="font-heading" style={{ fontSize: '2.5rem', color: cssVars.text, marginBottom: '16px' }}>Page not found</h1>
          <p style={{ color: cssVars.textSecondary, marginBottom: '24px' }}>This page doesn&apos;t exist or isn&apos;t published yet.</p>
          <Link to="/" style={{ color: cssVars.text, textDecoration: 'underline' }}>Go home</Link>
        </div>
      </div>
    );
  }

  const hero = page.hero || {};
  const connect = page.connect || {};
  const cards = (page.cards || []).filter(c => c.is_visible !== false).sort((a, b) => a.order - b.order);
  
  // Separate horizontal cards from column cards
  const announcementCard = cards.find(c => c.type === 'ANNOUNCEMENT');
  const investmentThesisCard = cards.find(c => c.type === 'INVESTMENT_THESIS');
  const statsCard = cards.find(c => c.type === 'STATS_GRID');
  const howIInvestCard = cards.find(c => c.type === 'HOW_I_INVEST');
  const investmentFocusCard = cards.find(c => c.type === 'INVESTMENT_FOCUS');
  const postInvestmentCard = cards.find(c => c.type === 'POST_INVESTMENT_SUPPORT');
  const highlightsCard = cards.find(c => c.type === 'HIGHLIGHTS');
  const carouselCard = cards.find(c => c.type === 'CAROUSEL');
  
  // Get horizontal Empty cards (those with isHorizontal: true in content)
  const horizontalEmptyCards = cards.filter(c => c.type === 'EMPTY' && c.content?.isHorizontal);
  const horizontalEmptyCardIds = new Set(horizontalEmptyCards.map(c => c.id));
  
  const otherCards = cards.filter(c => 
    c.type !== 'STATS_GRID' && 
    c.type !== 'HOW_I_INVEST' && 
    c.type !== 'INVESTMENT_FOCUS' && 
    c.type !== 'POST_INVESTMENT_SUPPORT' &&
    c.type !== 'ANNOUNCEMENT' &&
    c.type !== 'INVESTMENT_THESIS' &&
    c.type !== 'HIGHLIGHTS' &&
    c.type !== 'CAROUSEL' &&
    // Exclude horizontal Empty cards - they render in the horizontal sections
    !(c.type === 'EMPTY' && c.content?.isHorizontal)
  );
  const leftCards = otherCards.filter((_, i) => i % 2 === 0);
  const rightCards = otherCards.filter((_, i) => i % 2 === 1);

  // Get horizontal order from page data or use default
  const horizontalOrder = page.horizontal_order || ['announcement', 'carousel', 'investmentThesis', 'stats', 'howIInvest', 'investmentFocus', 'postInvestment'];
  
  // Check if connect section has content AND is in the horizontal order
  const hasConnectContent = connect.newsletter_url || connect.podcast_url || connect.course_url || connect.cta_link || (connect.custom_links && connect.custom_links.length > 0);
  const showConnectSection = hasConnectContent && horizontalOrder.includes('connect');

  // Split name for styling
  const nameParts = (hero.name || '').split(' ');
  const firstName = nameParts[0];
  const lastName = nameParts.slice(1).join(' ');

  return (
    <div style={{ minHeight: '100vh', background: cssVars.bg }}>
      <title>{hero.name ? `${hero.name} - Brief` : 'Brief Profile'}</title>

      {/* Subtle Login/Dashboard Link - Top Right */}
      <Link 
        to={user ? "/editor" : "/login"} 
        style={{
          position: 'fixed',
          top: '12px',
          right: '24px',
          fontSize: '13px',
          color: cssVars.textMuted,
          textDecoration: 'none',
          opacity: 0.6,
          transition: 'opacity 0.2s ease',
          zIndex: 100,
          fontWeight: '500',
          letterSpacing: '0.01em'
        }}
        onMouseEnter={(e) => e.target.style.opacity = '1'}
        onMouseLeave={(e) => e.target.style.opacity = '0.6'}
      >
        {user ? 'dashboard →' : 'login →'}
      </Link>

      {/* Page Container */}
      <div style={{ 
        width: '100%', 
        minHeight: '100vh', 
        padding: 'clamp(30px, 5vh, 60px) clamp(20px, 4vw, 60px)' 
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          
          {/* Hero Card */}
          <div 
            className="hero-card"
            style={{ 
              background: cssVars.bgWhite,
              borderRadius: '24px',
              padding: 'clamp(30px, 5vw, 50px)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 'clamp(30px, 5vw, 60px)',
              alignItems: 'center',
              marginBottom: '24px'
            }}
          >
            {/* Content */}
            <div className="hero-content">
              <h1 
                className="font-heading"
                style={{ 
                  fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                  fontWeight: '400',
                  color: cssVars.text,
                  lineHeight: '1.1',
                  letterSpacing: '-0.03em',
                  marginBottom: 'clamp(6px, 1vh, 10px)'
                }}
              >
                <span style={{ fontStyle: 'italic' }}>{firstName}</span> {lastName}
              </h1>
              
              <p style={{ 
                fontSize: 'clamp(1rem, 1.5vw, 1.15rem)',
                color: cssVars.textSecondary,
                marginBottom: 'clamp(8px, 1.5vh, 14px)',
                fontWeight: '600'
              }}>
                {hero.role}
              </p>

              {/* Info Row */}
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: '20px',
                marginBottom: 'clamp(16px, 2.5vh, 24px)',
                fontSize: '0.9rem'
              }}>
                {hero.location && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: cssVars.textSecondary }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVars.textMuted} strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    {hero.location}
                  </div>
                )}
                {hero.email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: cssVars.textSecondary }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVars.textMuted} strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    <a href={`mailto:${hero.email}`} style={{ color: cssVars.textSecondary, textDecoration: 'none' }}>
                      {hero.email}
                    </a>
                  </div>
                )}
                {/* Website link */}
                {(hero.website || hero.email) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: cssVars.textSecondary }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVars.textMuted} strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="2" y1="12" x2="22" y2="12"/>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                    </svg>
                    <a 
                      href={`https://${(hero.website || hero.email?.split('@')[1] || '').replace(/^https?:\/\//, '')}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: cssVars.textSecondary, textDecoration: 'none' }}
                    >
                      {hero.website || hero.email?.split('@')[1] || ''}
                    </a>
                  </div>
                )}
              </div>

              {/* Subtitle */}
              {hero.subtitle && (
                <p style={{ 
                  fontSize: 'clamp(0.95rem, 1.3vw, 1.05rem)',
                  color: cssVars.textSecondary,
                  lineHeight: '1.7',
                  maxWidth: '480px',
                  fontWeight: '300'
                }}>
                  {hero.subtitle}
                </p>
              )}

              {/* Badge */}
              {hero.badge && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: cssVars.accentLight,
                  color: cssVars.accent,
                  padding: '0.4rem 1rem',
                  borderRadius: '100px',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  letterSpacing: '0.02em',
                  marginTop: 'clamp(12px, 2vh, 18px)'
                }}>
                  <span>✦</span>
                  {hero.badge}
                </div>
              )}

              {/* Tags */}
              {hero.tags && hero.tags.length > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px', 
                  marginTop: hero.badge ? '12px' : 'clamp(12px, 2vh, 18px)'
                }}>
                  {hero.tags.map((tag, i) => {
                    const tagColor = getTagColor(tag, i);
                    return (
                      <span
                        key={i}
                        style={{
                          background: tagColor.bg,
                          color: tagColor.text,
                          padding: '0.35rem 0.85rem',
                          borderRadius: '100px',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}
                      >
                        {tag}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* App Buttons - Below tags with divider */}
              {page.apps?.filter(a => a.enabled).length > 0 && (
                <div style={{ marginTop: '32px' }}>
                  <div style={{
                    width: '100%',
                    maxWidth: '200px',
                    height: '1px',
                    background: 'linear-gradient(90deg, #d1d5db 0%, #e5e7eb 50%, transparent 100%)',
                    marginBottom: '28px'
                  }} />
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '12px'
                  }}>
                    {page.apps.filter(a => a.enabled).map((app) => (
                      <AppButton 
                        key={app.id} 
                        app={app} 
                        slug={slug}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Photo Section */}
            <div className="photo-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', alignSelf: 'flex-start', marginTop: '85px' }}>
              <div 
                style={{
                  width: 'clamp(180px, 20vw, 220px)',
                  aspectRatio: '1/1',
                  background: cssVars.bg,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(50px, 8vw, 70px)'
                }}
              >
                {hero.photo_url ? (
                  <img 
                    src={hero.photo_url} 
                    alt={hero.name} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  '👨‍💼'
                )}
              </div>
              
              {/* Social links under photo */}
              {(connect.linkedin_url || connect.twitter_url || connect.github_url || connect.instagram_url) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                  {connect.linkedin_url && (
                    <a href={connect.linkedin_url} target="_blank" rel="noopener noreferrer" 
                      style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', transition: 'background 0.2s' }}
                    >
                      <svg viewBox="0 0 24 24" style={{ width: '14px', height: '14px', fill: '#666' }}>
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                  )}
                  {connect.twitter_url && (
                    <a href={connect.twitter_url} target="_blank" rel="noopener noreferrer"
                      style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', transition: 'background 0.2s' }}
                    >
                      <svg viewBox="0 0 24 24" style={{ width: '13px', height: '13px', fill: '#666' }}>
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </a>
                  )}
                  {connect.github_url && (
                    <a href={connect.github_url} target="_blank" rel="noopener noreferrer"
                      style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', transition: 'background 0.2s' }}
                    >
                      <svg viewBox="0 0 24 24" style={{ width: '14px', height: '14px', fill: '#666' }}>
                        <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
                      </svg>
                    </a>
                  )}
                  {connect.instagram_url && (
                    <a href={connect.instagram_url} target="_blank" rel="noopener noreferrer"
                      style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', transition: 'background 0.2s' }}
                    >
                      <svg viewBox="0 0 24 24" style={{ width: '14px', height: '14px', fill: '#666' }}>
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                      </svg>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Highlights Section - Rendered just below hero */}
          {highlightsCard && highlightsCard.content?.items?.length > 0 && (
            <div 
              style={{
                background: cssVars.bgWhite,
                borderRadius: '16px',
                padding: '24px 30px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                marginBottom: '24px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  background: cssVars.bg,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.1rem'
                }}>
                  {highlightsCard.icon || '⭐'}
                </div>
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                  {highlightsCard.title || 'highlights'}
                </span>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {highlightsCard.content.items.map((item, i) => (
                  <li 
                    key={i} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '12px', 
                      marginBottom: i < highlightsCard.content.items.length - 1 ? '12px' : 0 
                    }}
                  >
                    <span style={{ 
                      color: cssVars.accent, 
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      marginTop: '2px'
                    }}>•</span>
                    <span style={{ 
                      fontSize: '0.95rem', 
                      color: cssVars.textSecondary, 
                      lineHeight: '1.6' 
                    }}>{typeof item === 'string' ? item : item.text || ''}</span>
                  </li>
                ))}
              </ul>
              {renderExtras(highlightsCard.extras)}
            </div>
          )}

          {/* Carousel Section - Rendered if not in horizontalOrder (fallback) */}
          {carouselCard && carouselCard.content?.items?.length > 0 && !horizontalOrder.includes('carousel') && (() => {
            const sizes = { 'S': { w: '160px', h: '110px' }, 'M': { w: '200px', h: '140px' }, 'L': { w: '260px', h: '180px' } };
            const sz = sizes[carouselCard.content?.cardSize] || sizes['S'];
            return (
            <div style={{ marginBottom: '24px' }}>
              {carouselCard.title && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{ width: '36px', height: '36px', background: cssVars.bgWhite, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    {carouselCard.icon && carouselCard.icon !== '▢' ? carouselCard.icon : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                        <rect x="2" y="6" width="6" height="12" rx="1"/>
                        <rect x="9" y="6" width="6" height="12" rx="1"/>
                        <rect x="16" y="6" width="6" height="12" rx="1"/>
                      </svg>
                    )}
                  </div>
                  <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                    {carouselCard.title || 'featured'}
                  </span>
                </div>
              )}
              <div className="carousel-container" style={{ display: 'flex', gap: '16px', overflowX: 'auto', paddingBottom: '8px', scrollSnapType: 'x mandatory', scrollBehavior: 'smooth', msOverflowStyle: 'none', scrollbarWidth: 'thin' }}>
                {carouselCard.content.items.map((item, i) => (
                  <a key={`public-carousel-${i}`} href={item.url || item.linkPreview?.url || '#'} target={item.url || item.linkPreview?.url ? '_blank' : '_self'} rel="noopener noreferrer"
                    style={{ flex: `0 0 ${sz.w}`, scrollSnapAlign: 'start', background: cssVars.bgWhite, borderRadius: '16px', overflow: 'hidden', textDecoration: 'none', transition: 'transform 0.3s ease, box-shadow 0.3s ease', cursor: item.url || item.linkPreview?.url ? 'pointer' : 'default', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                    onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
                  >
                    <div style={{ width: '100%', height: sz.h, overflow: 'hidden', background: item.image || item.linkPreview?.image ? 'transparent' : `linear-gradient(135deg, ${cssVars.accentLight} 0%, ${cssVars.bg} 100%)` }}>
                      {(item.image || item.linkPreview?.image) ? (
                        <img src={item.image || item.linkPreview?.image} alt={item.title || item.linkPreview?.title || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.background = `linear-gradient(135deg, ${cssVars.accentLight} 0%, ${cssVars.bg} 100%)`; }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={cssVars.textMuted} strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></svg>
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '10px 12px' }}>
                      <p style={{ fontSize: '0.8rem', fontWeight: '600', color: cssVars.text, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', margin: 0 }}>
                        {item.title || item.linkPreview?.title || 'Untitled'}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
              {renderExtras(carouselCard.extras)}
            </div>
          );
          })()}

          {/* Horizontal Sections - Rendered in order from horizontalOrder */}
          {horizontalOrder.map((sectionId) => {
            // Announcement Section - with golden glow effect
            if (sectionId === 'announcement') {
              if (!announcementCard || !announcementCard.content?.text) return null;
              return (
                <div 
                  key={sectionId}
                  style={{
                    background: 'linear-gradient(135deg, #FDF8F3 0%, #FAF0E6 100%)',
                    borderRadius: '16px',
                    padding: '20px 24px',
                    boxShadow: '0 0 15px rgba(217, 179, 147, 0.2), 0 4px 12px rgba(0,0,0,0.03)',
                    marginBottom: '24px',
                    border: '1px solid rgba(217, 179, 147, 0.25)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '1.5rem' }}>📢</span>
                    <span style={{ 
                      fontSize: '0.95rem', 
                      fontWeight: '500', 
                      color: '#78716C'
                    }}>
                      {announcementCard.content.text}
                    </span>
                  </div>
                  {renderExtras(announcementCard.extras, true)}
                </div>
              );
            }

            // Investment Thesis Section
            if (sectionId === 'investmentThesis') {
              if (!investmentThesisCard || !investmentThesisCard.content?.text) return null;
              return (
                <div 
                  key={sectionId}
                  style={{
                    background: cssVars.bgWhite,
                    borderRadius: '16px',
                    padding: '24px 30px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    marginBottom: '24px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      background: cssVars.bg,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.1rem'
                    }}>
                      📝
                    </div>
                    <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                      investment thesis
                    </span>
                  </div>
                  <p style={{ 
                    fontSize: '0.95rem', 
                    color: cssVars.textSecondary, 
                    lineHeight: '1.7'
                  }}>
                    {investmentThesisCard.content.text}
                  </p>
                  {renderExtras(investmentThesisCard.extras)}
                </div>
              );
            }

            // Stats / At A Glance Section
            if (sectionId === 'stats') {
              if (!statsCard) return null;
              return (
                <div 
                  key={sectionId}
                  style={{
                    background: cssVars.bgWhite,
                    borderRadius: '16px',
                    padding: '24px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    marginBottom: '24px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      background: cssVars.bg,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.1rem'
                    }}>
                      {statsCard.icon || '📊'}
                    </div>
                    <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                      {statsCard.title || 'at a glance'}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    {(statsCard.content?.stats || []).map((stat, i) => (
                      <div 
                        key={i} 
                        style={{ 
                          textAlign: 'center',
                          padding: '12px 8px',
                          background: cssVars.bg,
                          borderRadius: '10px'
                        }}
                      >
                        <p style={{ fontSize: '0.7rem', color: cssVars.textMuted, marginBottom: '4px', textTransform: 'lowercase' }}>
                          {stat.label}
                        </p>
                        <p style={{ fontSize: '0.95rem', fontWeight: '600', color: cssVars.text }}>
                          {stat.value}
                        </p>
                      </div>
                    ))}
                  </div>
                  {renderExtras(statsCard.extras)}
                </div>
              );
            }

            // Connect / Links Section
            if (sectionId === 'connect') {
              if (!showConnectSection) return null;
              return (
                <div 
                  key={sectionId}
                  style={{
                    background: cssVars.bgWhite,
                    borderRadius: '16px',
                    padding: '24px 30px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    marginBottom: '24px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{ width: '36px', height: '36px', background: cssVars.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                      🔗
                    </div>
                    <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                      links
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {connect.cta_label && connect.cta_link && (
                      <a 
                        href={connect.cta_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="cta-button"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '10px',
                          background: cssVars.accent,
                          color: 'white',
                          padding: '0.75rem 1.5rem',
                          borderRadius: '100px',
                          textDecoration: 'none',
                          fontWeight: '500',
                          fontSize: '0.9rem',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                          <polyline points="22,6 12,13 2,6"/>
                        </svg>
                        {connect.cta_label}
                      </a>
                    )}
                    {connect.newsletter_url && (
                      <a 
                        href={connect.newsletter_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: cssVars.text,
                          color: 'white',
                          padding: '0.65rem 1.25rem',
                          borderRadius: '100px',
                          textDecoration: 'none',
                          fontWeight: '500',
                          fontSize: '0.85rem',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Subscribe to Newsletter
                      </a>
                    )}
                    {connect.podcast_url && (
                      <a 
                        href={connect.podcast_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: cssVars.bg,
                          color: cssVars.text,
                          padding: '0.65rem 1.25rem',
                          borderRadius: '100px',
                          textDecoration: 'none',
                          fontWeight: '500',
                          fontSize: '0.85rem',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        🎙️ Podcast
                      </a>
                    )}
                    {connect.course_url && (
                      <a 
                        href={connect.course_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: cssVars.bg,
                          color: cssVars.text,
                          padding: '0.65rem 1.25rem',
                          borderRadius: '100px',
                          textDecoration: 'none',
                          fontWeight: '500',
                          fontSize: '0.85rem',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        📚 Course
                      </a>
                    )}
                    
                    {/* Custom Links without images (text links) */}
                    {connect.custom_links?.filter(link => link.url && !link.image_url).map((link, idx) => (
                      <a 
                        key={`custom-link-${idx}`}
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: cssVars.bg,
                          color: cssVars.text,
                          padding: '0.65rem 1.25rem',
                          borderRadius: '100px',
                          textDecoration: 'none',
                          fontWeight: '500',
                          fontSize: '0.85rem',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        🔗 {link.title || 'Link'}
                      </a>
                    ))}
                  </div>
                  
                  {/* Custom Links with images (card grid) */}
                  {connect.custom_links?.filter(link => link.url && link.image_url).length > 0 && (
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '16px', 
                      marginTop: '20px',
                      paddingTop: '20px',
                      borderTop: `1px solid ${cssVars.bg}`
                    }}>
                      {connect.custom_links?.filter(link => link.url && link.image_url).map((link, idx) => (
                        <a 
                          key={`custom-card-${idx}`}
                          href={link.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '140px',
                            background: cssVars.bg,
                            borderRadius: '12px',
                            overflow: 'hidden',
                            textDecoration: 'none',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                          }}
                          className="hover:shadow-md"
                        >
                          <div style={{ 
                            width: '100%', 
                            height: '100px', 
                            overflow: 'hidden'
                          }}>
                            <img 
                              src={link.image_url} 
                              alt={link.title}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          </div>
                          <div style={{ 
                            padding: '10px 12px',
                            fontSize: '0.8rem',
                            fontWeight: '500',
                            color: cssVars.text,
                            textAlign: 'center',
                            lineHeight: '1.3'
                          }}>
                            {link.title || 'Link'}
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            // How I Invest Section
            if (sectionId === 'howIInvest') {
              if (!howIInvestCard) return null;
              return (
                <div 
                  key={sectionId}
                  style={{
                    background: cssVars.bgWhite,
                    borderRadius: '16px',
                    padding: '24px 30px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    marginBottom: '24px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{ width: '36px', height: '36px', background: cssVars.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                      {howIInvestCard.icon || '💰'}
                    </div>
                    <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                      {howIInvestCard.title || 'how I invest'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'flex-start' }}>
                    {howIInvestCard.content?.lead_follow?.length > 0 && (
                      <div>
                        <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'lowercase', letterSpacing: '0.03em', display: 'block', marginBottom: '8px' }}>lead / follow</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {howIInvestCard.content.lead_follow.map((v, i) => (
                            <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{v}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {howIInvestCard.content?.primary_stage?.length > 0 && (
                      <div>
                        <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'lowercase', letterSpacing: '0.03em', display: 'block', marginBottom: '8px' }}>stage</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {howIInvestCard.content.primary_stage.map((v, i) => (
                            <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{v}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {howIInvestCard.content?.check_size?.length > 0 && (
                      <div>
                        <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'lowercase', letterSpacing: '0.03em', display: 'block', marginBottom: '8px' }}>check size</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {howIInvestCard.content.check_size.map((v, i) => (
                            <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{v}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {howIInvestCard.content?.ownership_target?.length > 0 && (
                      <div>
                        <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'lowercase', letterSpacing: '0.03em', display: 'block', marginBottom: '8px' }}>ownership</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {howIInvestCard.content.ownership_target.map((v, i) => (
                            <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{v}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {howIInvestCard.content?.hardware_software?.length > 0 && (
                      <div>
                        <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'lowercase', letterSpacing: '0.03em', display: 'block', marginBottom: '8px' }}>hw / sw</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {howIInvestCard.content.hardware_software.map((v, i) => (
                            <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{v}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {howIInvestCard.content?.b2b_b2c?.length > 0 && (
                      <div>
                        <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'lowercase', letterSpacing: '0.03em', display: 'block', marginBottom: '8px' }}>model</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {howIInvestCard.content.b2b_b2c.map((v, i) => (
                            <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{v}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {howIInvestCard.content?.geo_focus?.length > 0 && (
                      <div>
                        <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'lowercase', letterSpacing: '0.03em', display: 'block', marginBottom: '8px' }}>geo focus</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {howIInvestCard.content.geo_focus.map((v, i) => (
                            <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{v}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {renderExtras(howIInvestCard.extras)}
                </div>
              );
            }

            // Investment Focus Section
            if (sectionId === 'investmentFocus') {
              if (!investmentFocusCard) return null;
              return (
                <div 
                  key={sectionId}
                  style={{
                    background: cssVars.bgWhite,
                    borderRadius: '16px',
                    padding: '24px 30px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    marginBottom: '24px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{ width: '36px', height: '36px', background: cssVars.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                      {investmentFocusCard.icon || '🎯'}
                    </div>
                    <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                      {investmentFocusCard.title || 'investment focus'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '32px' }}>
                    {investmentFocusCard.content?.invest_in?.length > 0 && (
                      <div>
                        <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>I Invest In</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {investmentFocusCard.content.invest_in.map((v, i) => (
                            <span key={i} style={{ background: '#D1FAE5', color: '#065F46', padding: '6px 12px', borderRadius: '100px', fontSize: '0.8rem', fontWeight: '500' }}>{v}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {investmentFocusCard.content?.do_not_invest_in?.length > 0 && (
                      <div>
                        <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>I Don&apos;t Invest In</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {investmentFocusCard.content.do_not_invest_in.map((v, i) => (
                            <span key={i} style={{ background: '#FEE2E2', color: '#991B1B', padding: '6px 12px', borderRadius: '100px', fontSize: '0.8rem', fontWeight: '500' }}>{v}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {renderExtras(investmentFocusCard.extras)}
                </div>
              );
            }

            // Post-Investment Support Section
            if (sectionId === 'postInvestment') {
              if (!postInvestmentCard) return null;
              return (
                <div 
                  key={sectionId}
                  style={{
                    background: cssVars.bgWhite,
                    borderRadius: '16px',
                    padding: '24px 30px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    marginBottom: '24px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{ width: '36px', height: '36px', background: cssVars.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                      {postInvestmentCard.icon || '🤝'}
                    </div>
                    <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                      {postInvestmentCard.title || 'post-investment support'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {(postInvestmentCard.content?.support_areas || []).map((area, i) => (
                      <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{area}</span>
                    ))}
                  </div>
                  {renderExtras(postInvestmentCard.extras)}
                </div>
              );
            }

            // Carousel Section - horizontal scrolling cards with no background
            if (sectionId === 'carousel') {
              if (!carouselCard || !carouselCard.content?.items?.length) return null;
              const sizes = { 'S': { w: '160px', h: '110px' }, 'M': { w: '200px', h: '140px' }, 'L': { w: '260px', h: '180px' } };
              const sz = sizes[carouselCard.content?.cardSize] || sizes['S'];
              return (
                <div 
                  key={sectionId}
                  style={{ marginBottom: '24px' }}
                >
                  {/* Optional title row */}
                  {carouselCard.title && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        background: cssVars.bgWhite,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.1rem',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                      }}>
                        {carouselCard.icon && carouselCard.icon !== '▢' ? carouselCard.icon : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                            <rect x="2" y="6" width="6" height="12" rx="1"/>
                            <rect x="9" y="6" width="6" height="12" rx="1"/>
                            <rect x="16" y="6" width="6" height="12" rx="1"/>
                          </svg>
                        )}
                      </div>
                      <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                        {carouselCard.title || 'featured'}
                      </span>
                    </div>
                  )}
                  
                  {/* Carousel items - no background container */}
                  <div 
                    className="carousel-container"
                    style={{ 
                      display: 'flex', 
                      gap: '16px', 
                      overflowX: 'auto', 
                      paddingBottom: '8px',
                      scrollSnapType: 'x mandatory',
                      scrollBehavior: 'smooth',
                      msOverflowStyle: 'none',
                      scrollbarWidth: 'thin'
                    }}
                  >
                    {carouselCard.content.items.map((item, i) => (
                      <a
                        key={`carousel-public-loop-${i}`}
                        href={item.url || item.linkPreview?.url || '#'}
                        target={item.url || item.linkPreview?.url ? '_blank' : '_self'}
                        rel="noopener noreferrer"
                        style={{
                          flex: `0 0 ${sz.w}`,
                          scrollSnapAlign: 'start',
                          background: cssVars.bgWhite,
                          borderRadius: '16px',
                          overflow: 'hidden',
                          textDecoration: 'none',
                          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                          cursor: item.url || item.linkPreview?.url ? 'pointer' : 'default',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                        }}
                      >
                        {/* Image */}
                        <div style={{ 
                          width: '100%', 
                          height: sz.h, 
                          overflow: 'hidden',
                          background: item.image || item.linkPreview?.image 
                            ? 'transparent' 
                            : `linear-gradient(135deg, ${cssVars.accentLight} 0%, ${cssVars.bg} 100%)`
                        }}>
                          {(item.image || item.linkPreview?.image) ? (
                            <img 
                              src={item.image || item.linkPreview?.image} 
                              alt={item.title || item.linkPreview?.title || ''} 
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.style.background = `linear-gradient(135deg, ${cssVars.accentLight} 0%, ${cssVars.bg} 100%)`;
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={cssVars.textMuted} strokeWidth="1.5">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                <circle cx="8.5" cy="8.5" r="1.5"/>
                                <polyline points="21,15 16,10 5,21"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        {/* Title */}
                        <div style={{ padding: '10px 12px' }}>
                          <p style={{ 
                            fontSize: '0.8rem', 
                            fontWeight: '600', 
                            color: cssVars.text,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            margin: 0
                          }}>
                            {item.title || item.linkPreview?.title || 'Untitled'}
                          </p>
                        </div>
                      </a>
                    ))}
                  </div>
                  {renderExtras(carouselCard.extras)}
                </div>
              );
            }

            // Horizontal Empty Card
            if (sectionId.startsWith('empty_')) {
              const emptyCardId = sectionId.replace('empty_', '');
              const emptyCard = horizontalEmptyCards.find(c => c.id === emptyCardId);
              if (!emptyCard || !emptyCard.content?.fields?.length) return null;
              
              return (
                <div 
                  key={sectionId}
                  style={{
                    background: cssVars.bgWhite,
                    borderRadius: '20px',
                    padding: '24px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    marginBottom: '24px'
                  }}
                >
                  {/* Empty Card Header - show title and icon if present */}
                  {(emptyCard.title || emptyCard.icon) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                      {emptyCard.icon && (
                        <div style={{
                          width: '36px',
                          height: '36px',
                          background: cssVars.bg,
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.1rem'
                        }}>
                          {emptyCard.icon}
                        </div>
                      )}
                      {emptyCard.title && (
                        <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                          {emptyCard.title}
                        </span>
                      )}
                    </div>
                  )}
                  {/* Empty Card Content - group consecutive links */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {(() => {
                      const groupedFields = [];
                      let currentLinkGroup = [];
                      
                      emptyCard.content.fields.forEach((field) => {
                        if (field.type === 'link' && field.url) {
                          currentLinkGroup.push(field);
                        } else {
                          if (currentLinkGroup.length > 0) {
                            groupedFields.push({ type: 'linkGroup', links: [...currentLinkGroup] });
                            currentLinkGroup = [];
                          }
                          groupedFields.push(field);
                        }
                      });
                      
                      if (currentLinkGroup.length > 0) {
                        groupedFields.push({ type: 'linkGroup', links: currentLinkGroup });
                      }
                      
                      return groupedFields.map((item, i) => (
                        <div key={i}>
                          {item.type === 'text' && item.value && (
                            <p style={{ 
                              margin: 0, 
                              fontSize: '0.95rem', 
                              color: cssVars.text,
                              fontWeight: item.isBold ? '600' : '400',
                              lineHeight: '1.6'
                            }}>
                              {item.value}
                            </p>
                          )}
                          {item.type === 'tags' && item.value?.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {item.value.map((tag, ti) => (
                                <span 
                                  key={ti}
                                  style={{
                                    padding: '6px 14px',
                                    background: cssVars.accentLight,
                                    color: cssVars.accent,
                                    borderRadius: '100px',
                                    fontSize: '0.8rem',
                                    fontWeight: '500'
                                  }}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          {item.type === 'linkGroup' && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {item.links.map((link, li) => (
                                <a 
                                  key={link.id || li}
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '10px 16px',
                                    background: cssVars.bgWhite,
                                    border: `1px solid ${cssVars.border}`,
                                    borderRadius: '12px',
                                    color: cssVars.accent,
                                    fontSize: '0.9rem',
                                    textDecoration: 'none',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.borderColor = cssVars.accent;
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.borderColor = cssVars.border;
                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                                  }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                                    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                                  </svg>
                                  {link.value || link.url}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ));
                    })()}
                  </div>
                  {renderExtras(emptyCard.extras)}
                </div>
              );
            }

            return null;
          })}

          {/* Two Column Layout */}
          <div 
            className="two-columns"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '24px'
            }}
          >
            {/* Left Column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {leftCards.map((card, i) => (
                <InfoCard key={card.id} card={card} />
              ))}
              {leftCards.length === 0 && (
                <EmptyCard title="advice for founders" icon="💡" />
              )}
            </div>

            {/* Right Column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {rightCards.map((card, i) => (
                <InfoCard key={card.id} card={card} />
              ))}
              {rightCards.length === 0 && (
                <>
                  <EmptyCard title="content I like" icon="📚" />
                  <EmptyCard title="outside of work" icon="☀️" />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer style={{
          textAlign: 'center',
          padding: '40px 20px 30px',
          marginTop: '20px'
        }}>
          <p style={{
            fontSize: '0.85rem',
            color: cssVars.textMuted,
            fontWeight: '400'
          }}>
            made with{' '}
            <a 
              href="https://brief.link" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                color: cssVars.accent,
                textDecoration: 'none',
                fontWeight: '500'
              }}
            >
              brief
            </a>
          </p>
        </footer>
      </div>

      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .hero-card {
            grid-template-columns: 1fr !important;
            text-align: center;
          }
          .photo-section {
            order: -1;
            margin-top: 35px !important;
          }
          .hero-content > div {
            justify-content: center;
          }
          .two-columns {
            grid-template-columns: 1fr !important;
          }
        }
        .social-icon-link:hover {
          background: ${cssVars.accentLight} !important;
          color: ${cssVars.accent} !important;
          transform: translateY(-2px);
        }
        .cta-button:hover {
          background: #d44a35 !important;
          transform: translateY(-2px);
        }
        a:hover {
          color: ${cssVars.accent};
        }
        /* Carousel scrollbar styling */
        .carousel-container::-webkit-scrollbar {
          height: 6px;
        }
        .carousel-container::-webkit-scrollbar-track {
          background: ${cssVars.bg};
          border-radius: 3px;
        }
        .carousel-container::-webkit-scrollbar-thumb {
          background: ${cssVars.border};
          border-radius: 3px;
        }
        .carousel-container::-webkit-scrollbar-thumb:hover {
          background: ${cssVars.textMuted};
        }
      `}</style>
    </div>
  );
}

function SocialIcon({ href, type }) {
  const icons = {
    linkedin: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    twitter: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    github: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
      </svg>
    ),
    instagram: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
    website: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
        <circle cx="12" cy="12" r="10"/>
        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
  };

  return (
    <a 
      href={href} 
      target="_blank" 
      rel="noopener noreferrer"
      className="social-icon-link"
      style={{
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        background: cssVars.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: cssVars.text,
        textDecoration: 'none',
        transition: 'all 0.2s ease'
      }}
    >
      {icons[type]}
    </a>
  );
}

function InfoCard({ card }) {
  return (
    <div 
      style={{
        background: cssVars.bgWhite,
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}
    >
      {/* Card Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <div style={{
          width: '36px',
          height: '36px',
          background: cssVars.bg,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.1rem'
        }}>
          {card.icon}
        </div>
        <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
          {card.title}
        </span>
      </div>

      {/* Card Content */}
      {card.type === 'STATS_GRID' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          {(card.content?.stats || []).map((stat, i) => (
            <div 
              key={i} 
              style={{ 
                textAlign: 'center',
                padding: '12px 8px',
                background: cssVars.bg,
                borderRadius: '10px'
              }}
            >
              <p style={{ fontSize: '0.7rem', color: cssVars.textMuted, marginBottom: '4px', textTransform: 'lowercase' }}>
                {stat.label}
              </p>
              <p style={{ fontSize: '0.95rem', fontWeight: '600', color: cssVars.text }}>
                {stat.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {card.type === 'ADVICE' && (
        <p style={{ fontSize: '0.95rem', color: cssVars.textSecondary, lineHeight: '1.6' }}>
          {card.content?.text}
        </p>
      )}

      {card.type === 'JOURNEY_TIMELINE' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0', position: 'relative' }}>
          {(card.content?.items || []).map((item, i, arr) => (
            <div 
              key={i} 
              style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '14px',
                padding: '12px 0',
                paddingTop: i === 0 ? '0' : '12px',
                paddingBottom: i === arr.length - 1 ? '0' : '12px',
                position: 'relative'
              }}
            >
              {/* Timeline connector line */}
              {i < arr.length - 1 && (
                <div style={{
                  position: 'absolute',
                  left: '4px',
                  top: i === 0 ? '5px' : '17px',
                  width: '2px',
                  height: 'calc(100% - 8px)',
                  background: cssVars.border
                }} />
              )}
              {/* Marker */}
              <div style={{
                width: '10px',
                height: '10px',
                background: item.is_current ? cssVars.accent : cssVars.border,
                borderRadius: '50%',
                flexShrink: 0,
                marginTop: '5px',
                position: 'relative',
                zIndex: 1
              }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {/* Company name - bold */}
                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: cssVars.text }}>
                  {item.title || item.company}
                </span>
                {/* Role - light gray */}
                {(item.subtitle || item.role) && (
                  <span style={{ fontSize: '0.8rem', color: cssVars.textMuted, fontWeight: '400' }}>
                    {item.subtitle || item.role}
                  </span>
                )}
                {/* Description/one-liner - light gray, smaller */}
                {item.description && (
                  <span style={{ 
                    fontSize: '0.78rem', 
                    color: cssVars.textMuted, 
                    fontWeight: '400',
                    lineHeight: '1.4',
                    marginTop: '2px',
                    opacity: 0.85
                  }}>
                    {item.description}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {card.type === 'CONTENT_LIST' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {(card.content?.items || []).map((item, i) => {
            const isPodcast = item.type === 'podcast';
            const isNewsletter = item.type === 'newsletter';
            const isBook = item.type === 'book';
            const hasLink = item.url;
            const isLinkedContent = (isPodcast || isNewsletter || isBook) && hasLink;
            
            // Color scheme based on type
            const colorScheme = isPodcast 
              ? { bg: 'linear-gradient(135deg, #FDF4FF 0%, #FAE8FF 100%)', border: 'rgba(168, 85, 247, 0.15)', text: '#7C3AED', accent: '#A855F7', iconBg: 'linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)' }
              : isNewsletter 
                ? { bg: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)', border: 'rgba(249, 115, 22, 0.15)', text: '#C2410C', accent: '#F97316', iconBg: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' }
                : isBook
                  ? { bg: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)', border: 'rgba(16, 185, 129, 0.15)', text: '#065F46', accent: '#10B981', iconBg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }
                  : null;
            
            // Content wrapper - clickable if has link
            const ContentWrapper = hasLink ? 'a' : 'div';
            const wrapperProps = hasLink ? {
              href: item.url,
              target: '_blank',
              rel: 'noopener noreferrer',
              style: { textDecoration: 'none' }
            } : {};
            
            return (
              <ContentWrapper 
                key={i}
                {...wrapperProps}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: isLinkedContent ? '10px 14px' : '0',
                  background: isLinkedContent ? colorScheme.bg : 'transparent',
                  borderRadius: isLinkedContent ? '12px' : '0',
                  border: isLinkedContent ? `1px solid ${colorScheme.border}` : 'none',
                  transition: 'all 0.2s ease',
                  cursor: hasLink ? 'pointer' : 'default',
                }}
                className={hasLink ? 'hover:shadow-sm' : ''}
              >
                {/* Type icon for linked content */}
                {isLinkedContent ? (
                  <div style={{
                    width: '32px',
                    height: '32px',
                    background: colorScheme.iconBg,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {isPodcast ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                        <path d="M12 1a9 9 0 0 0-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7a9 9 0 0 0-9-9z"/>
                      </svg>
                    ) : isNewsletter ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6" fill="none" stroke="white" strokeWidth="2"/>
                      </svg>
                    ) : isBook ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" fill="white"/>
                      </svg>
                    ) : null}
                  </div>
                ) : (
                  <span style={{ 
                    fontSize: '0.75rem', 
                    color: cssVars.textMuted, 
                    minWidth: '55px',
                    textTransform: 'lowercase'
                  }}>
                    {item.type}
                  </span>
                )}
                
                {/* Name and link indicator */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ 
                    fontWeight: '500', 
                    color: isLinkedContent ? colorScheme.text : cssVars.text, 
                    fontSize: '0.9rem',
                    display: 'block'
                  }}>
                    {item.name}
                  </span>
                  {isLinkedContent && (
                    <span style={{ 
                      fontSize: '0.7rem', 
                      color: colorScheme.accent,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginTop: '2px'
                    }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/>
                      </svg>
                      {isPodcast ? 'Listen to Podcast' : isNewsletter ? 'Read Newsletter' : isBook ? 'Read Book' : 'View Link'}
                    </span>
                  )}
                </div>
                
                {/* External link arrow for linked items */}
                {hasLink && (
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke={isLinkedContent ? colorScheme.accent : cssVars.textMuted}
                    strokeWidth="2"
                    style={{ flexShrink: 0, opacity: 0.6 }}
                  >
                    <path d="M7 17L17 7M17 7H7M17 7V17"/>
                  </svg>
                )}
              </ContentWrapper>
            );
          })}
        </div>
      )}

      {card.type === 'OUTSIDE_OF_WORK' && (
        <div>
          {(card.content?.items || []).map((item, i, arr) => (
            <div 
              key={i} 
              style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '14px',
                padding: '14px 0',
                paddingTop: i === 0 ? '0' : '14px',
                paddingBottom: i === arr.length - 1 ? '0' : '14px',
                borderBottom: i < arr.length - 1 ? `1px solid ${cssVars.border}` : 'none'
              }}
            >
              {item.icon && (
                <div style={{
                  width: '36px',
                  height: '36px',
                  background: cssVars.bg,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.1rem',
                  flexShrink: 0
                }}>
                  {item.icon}
                </div>
              )}
              <p style={{ fontSize: '0.9rem', color: cssVars.textSecondary, lineHeight: '1.5', paddingTop: item.icon ? '0' : '8px' }}>
                {item.text}
              </p>
            </div>
          ))}
        </div>
      )}

      {card.type === 'HIGHLIGHTS' && (
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          {(card.content?.items || []).map((item, i) => (
            <li key={i} style={{ fontSize: '0.95rem', color: cssVars.textSecondary, marginBottom: '8px', lineHeight: '1.6' }}>
              {typeof item === 'string' ? item : item.text}
            </li>
          ))}
        </ul>
      )}

      {card.type === 'LINKS' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {(card.content?.items || []).map((item, i) => (
            <a 
              key={i} 
              href={item.url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                color: cssVars.text, 
                textDecoration: 'none', 
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              {item.label}
            </a>
          ))}
        </div>
      )}

      {card.type === 'QUOTE' && (
        <blockquote style={{ borderLeft: `3px solid ${cssVars.text}`, paddingLeft: '16px', margin: 0 }}>
          <p style={{ fontSize: '1rem', fontStyle: 'italic', color: cssVars.text, lineHeight: '1.7' }}>
            &ldquo;{card.content?.text}&rdquo;
          </p>
          {card.content?.author && (
            <cite style={{ display: 'block', fontSize: '0.85rem', color: cssVars.textMuted, marginTop: '12px', fontStyle: 'normal' }}>
              — {card.content.author}
            </cite>
          )}
        </blockquote>
      )}

      {card.type === 'HOW_I_INVEST' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {card.content?.lead_follow && (
            <div>
              <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lead / Follow</span>
              <p style={{ fontSize: '0.9rem', fontWeight: '600', color: cssVars.text }}>{card.content.lead_follow}</p>
            </div>
          )}
          {card.content?.primary_stage && (
            <div>
              <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Primary Stage</span>
              <p style={{ fontSize: '0.9rem', fontWeight: '600', color: cssVars.text }}>{card.content.primary_stage}</p>
            </div>
          )}
          {card.content?.check_size && (
            <div>
              <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Check Size</span>
              <p style={{ fontSize: '0.9rem', fontWeight: '600', color: cssVars.text }}>{card.content.check_size}</p>
            </div>
          )}
          {card.content?.ownership_target && (
            <div>
              <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ownership Target</span>
              <p style={{ fontSize: '0.9rem', fontWeight: '600', color: cssVars.text }}>{card.content.ownership_target}</p>
            </div>
          )}
          {card.content?.hardware_software && (
            <div>
              <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>HW / SW</span>
              <p style={{ fontSize: '0.9rem', fontWeight: '600', color: cssVars.text }}>{card.content.hardware_software}</p>
            </div>
          )}
          {card.content?.b2b_b2c && (
            <div>
              <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>B2B / B2C</span>
              <p style={{ fontSize: '0.9rem', fontWeight: '600', color: cssVars.text }}>{card.content.b2b_b2c}</p>
            </div>
          )}
          {card.content?.geo_focus && (
            <div>
              <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Geo Focus</span>
              <p style={{ fontSize: '0.9rem', fontWeight: '600', color: cssVars.text }}>{card.content.geo_focus}</p>
            </div>
          )}
          {card.content?.can_also_enter && card.content.can_also_enter !== '-' && (
            <div>
              <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Can Also Enter</span>
              <p style={{ fontSize: '0.9rem', fontWeight: '600', color: cssVars.text }}>{card.content.can_also_enter}</p>
            </div>
          )}
        </div>
      )}

      {card.type === 'INVESTMENT_FOCUS' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {card.content?.invest_in && card.content.invest_in.length > 0 && (
            <div>
              <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '10px' }}>I Invest In</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {card.content.invest_in.map((focus, i) => (
                  <span key={i} style={{ background: '#D1FAE5', color: '#065F46', padding: '6px 12px', borderRadius: '100px', fontSize: '0.8rem', fontWeight: '500' }}>{focus}</span>
                ))}
              </div>
            </div>
          )}
          {card.content?.do_not_invest_in && card.content.do_not_invest_in.length > 0 && (
            <div>
              <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '10px' }}>I Don&apos;t Invest In</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {card.content.do_not_invest_in.map((focus, i) => (
                  <span key={i} style={{ background: '#FEE2E2', color: '#991B1B', padding: '6px 12px', borderRadius: '100px', fontSize: '0.8rem', fontWeight: '500' }}>{focus}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty Card - renders flexible fields with grouped links */}
      {card.type === 'EMPTY' && card.content?.fields && card.content.fields.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Empty Card Header - show title and icon if present */}
          {(card.title || card.icon) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              {card.icon && (
                <div style={{
                  width: '36px',
                  height: '36px',
                  background: cssVars.bg,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.1rem'
                }}>
                  {card.icon}
                </div>
              )}
              {card.title && (
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                  {card.title}
                </span>
              )}
            </div>
          )}
          {(() => {
            // Group consecutive links together
            const groupedFields = [];
            let currentLinkGroup = [];
            
            card.content.fields.forEach((field) => {
              if (field.type === 'link' && field.url) {
                currentLinkGroup.push(field);
              } else {
                if (currentLinkGroup.length > 0) {
                  groupedFields.push({ type: 'linkGroup', links: [...currentLinkGroup] });
                  currentLinkGroup = [];
                }
                groupedFields.push(field);
              }
            });
            
            // Don't forget trailing links
            if (currentLinkGroup.length > 0) {
              groupedFields.push({ type: 'linkGroup', links: currentLinkGroup });
            }
            
            return groupedFields.map((item, i) => (
              <div key={i}>
                {item.type === 'text' && item.value && (
                  <p style={{ 
                    margin: 0, 
                    fontSize: '0.95rem', 
                    color: cssVars.text,
                    fontWeight: item.isBold ? '600' : '400',
                    lineHeight: '1.6'
                  }}>
                    {item.value}
                  </p>
                )}
                {item.type === 'tags' && item.value?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {item.value.map((tag, ti) => (
                      <span 
                        key={ti}
                        style={{
                          padding: '6px 14px',
                          background: cssVars.accentLight,
                          color: cssVars.accent,
                          borderRadius: '100px',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                {item.type === 'linkGroup' && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {item.links.map((link, li) => (
                      <a 
                        key={link.id || li}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 16px',
                          background: cssVars.bgWhite,
                          border: `1px solid ${cssVars.border}`,
                          borderRadius: '12px',
                          color: cssVars.accent,
                          fontSize: '0.9rem',
                          textDecoration: 'none',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.borderColor = cssVars.accent;
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.borderColor = cssVars.border;
                          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                          <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                        </svg>
                        {link.value || link.url}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ));
          })()}
        </div>
      )}

      {/* Carousel Card - horizontal scrolling showcase */}
      {card.type === 'CAROUSEL' && (
        <div 
          className="carousel-container"
          style={{ 
            display: 'flex', 
            gap: '16px', 
            overflowX: 'auto', 
            paddingBottom: '12px',
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            marginLeft: '-8px',
            marginRight: '-8px',
            paddingLeft: '8px',
            paddingRight: '8px',
            msOverflowStyle: 'none',
            scrollbarWidth: 'thin'
          }}
        >
          {(card.content?.items || []).map((item, i) => (
            <a
              key={i}
              href={item.url || item.linkPreview?.url || '#'}
              target={item.url || item.linkPreview?.url ? '_blank' : '_self'}
              rel="noopener noreferrer"
              style={{
                flex: '0 0 180px',
                scrollSnapAlign: 'start',
                background: cssVars.bg,
                borderRadius: '16px',
                overflow: 'hidden',
                textDecoration: 'none',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                cursor: item.url || item.linkPreview?.url ? 'pointer' : 'default',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
              }}
            >
              {/* Image */}
              <div style={{ 
                width: '100%', 
                height: '120px', 
                overflow: 'hidden',
                background: item.image || item.linkPreview?.image 
                  ? 'transparent' 
                  : `linear-gradient(135deg, ${cssVars.accentLight} 0%, ${cssVars.bg} 100%)`
              }}>
                {(item.image || item.linkPreview?.image) ? (
                  <img 
                    src={item.image || item.linkPreview?.image} 
                    alt={item.title || item.linkPreview?.title || ''} 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.style.background = `linear-gradient(135deg, ${cssVars.accentLight} 0%, ${cssVars.bg} 100%)`;
                    }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={cssVars.textMuted} strokeWidth="1.5">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                  </div>
                )}
              </div>
              {/* Title */}
              <div style={{ padding: '12px 14px' }}>
                <p style={{ 
                  fontSize: '0.85rem', 
                  fontWeight: '600', 
                  color: cssVars.text,
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  margin: 0
                }}>
                  {item.title || item.linkPreview?.title || 'Untitled'}
                </p>
                {item.linkPreview?.description && (
                  <p style={{ 
                    fontSize: '0.75rem', 
                    color: cssVars.textMuted,
                    marginTop: '4px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {item.linkPreview.description}
                  </p>
                )}
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Card Extras - Free text, Tags, Links */}
      {card.extras && (card.extras.freeText || card.extras.tags?.length > 0 || card.extras.links?.length > 0) && (
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${cssVars.border}` }}>
          {/* Free text */}
          {card.extras.freeText && (
            <p style={{ fontSize: '0.9rem', color: cssVars.textSecondary, lineHeight: 1.6, marginBottom: card.extras.tags?.length || card.extras.links?.length ? '12px' : 0 }}>
              {card.extras.freeText}
            </p>
          )}

          {/* Tags */}
          {card.extras.tags?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: card.extras.links?.length ? '12px' : 0 }}>
              {card.extras.tags.map((tag, i) => {
                const color = getTagColor(tag);
                return (
                  <span
                    key={i}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '100px',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      backgroundColor: color.bg,
                      color: color.text
                    }}
                  >
                    {tag}
                  </span>
                );
              })}
            </div>
          )}

          {/* Links with previews */}
          {card.extras.links?.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {card.extras.links.filter(l => l.url).map((link, i) => (
                <a
                  key={i}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: `1px solid ${cssVars.border}`,
                    backgroundColor: cssVars.bg,
                    textDecoration: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = cssVars.borderHover;
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = cssVars.border;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {link.image && (
                    <img
                      src={link.image}
                      alt=""
                      style={{
                        width: '48px',
                        height: '48px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        flexShrink: 0
                      }}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ 
                        fontWeight: '500', 
                        fontSize: '0.875rem', 
                        color: cssVars.text,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {link.title || link.url}
                      </span>
                      <svg 
                        width="12" 
                        height="12" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke={cssVars.textMuted} 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        style={{ flexShrink: 0 }}
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </div>
                    {link.description && (
                      <p style={{ 
                        fontSize: '0.75rem', 
                        color: cssVars.textMuted, 
                        marginTop: '2px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {link.description}
                      </p>
                    )}
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyCard({ title, icon }) {
  return (
    <div 
      style={{
        background: cssVars.bgWhite,
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        opacity: 0.6
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <div style={{
          width: '36px',
          height: '36px',
          background: cssVars.bg,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.1rem'
        }}>
          {icon}
        </div>
        <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
          {title}
        </span>
      </div>
      <div style={{ 
        height: '60px', 
        background: cssVars.bg, 
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: cssVars.textMuted,
        fontSize: '0.85rem'
      }}>
        Coming soon...
      </div>
    </div>
  );
}
